import React, { useRef } from 'react';
import { Card, Row, Col, Button, Badge, Alert } from 'react-bootstrap';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';
import html2pdf from 'html2pdf.js';

interface ScanResultsProps {
  data: any;
  userData?: any;
  onNewScan?: () => void;
  onDownload?: () => void;
}

// Metric classification types
interface MetricRange {
  min: number;
  max: number;
  label: string;
}

interface MetricInfo {
  value: number | null;
  unit: string;
  rangeText: string;
  badge: 'LOW' | 'NORMAL' | 'HIGH';
  detailedLabel: string;
  meaning?: string;
  suggestion?: string;
}

const ScanResults: React.FC<ScanResultsProps> = ({ data, userData, onNewScan, onDownload }) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handleDownloadPDF = async () => {
    const element = printRef.current;
    if (!element) return;

    // Get user name or scan ID for filename
    const userName = userData?.name || data.user_name || data.name || 'Patient';
    
    // Handle date parsing more robustly
    let dateStr = '';
    try {
      const scanDateValue = data.scan_completion_time || data.timestamp || data.scan_date;
      let scanDate: Date | null = null;
      
      if (scanDateValue) {
        if (typeof scanDateValue === 'number') {
          // If it's a timestamp in seconds (unix timestamp), convert to milliseconds
          scanDate = new Date(scanDateValue > 9999999999 ? scanDateValue : scanDateValue * 1000);
        } else if (typeof scanDateValue === 'string') {
          scanDate = new Date(scanDateValue);
        }
      }
      
      // Check if date is valid, otherwise use current date
      if (scanDate && !isNaN(scanDate.getTime())) {
        dateStr = scanDate.toISOString().split('T')[0];
      } else {
        dateStr = new Date().toISOString().split('T')[0];
      }
    } catch (error) {
      console.warn('Error parsing scan date:', error);
      dateStr = new Date().toISOString().split('T')[0];
    }
    
    const filename = `Health_Report_${userName.replace(/\s+/g, '_')}_${dateStr}.pdf`;

    // Clone the element to avoid modifying the original
    const clonedElement = element.cloneNode(true) as HTMLElement;
    
    // Hide buttons in the cloned element
    const buttons = clonedElement.querySelectorAll('.hide-on-print');
    buttons.forEach((btn) => {
      (btn as HTMLElement).style.display = 'none';
    });

    // PDF options optimized for mobile and readability
    const opt = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename: filename,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        scrollY: 0,
        scrollX: 0
      },
      jsPDF: { 
        unit: 'mm' as const, 
        format: 'a4' as const, 
        orientation: 'portrait' as const,
        compress: true
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'] as const,
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.page-break-inside-avoid'
      }
    };

    try {
      // Show loading indicator
      const button = document.querySelector('.download-pdf-btn') as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Generating PDF...';
      }

      console.log('Starting PDF generation...');
      
      // Create the PDF worker
      const pdfWorker = html2pdf().set(opt).from(clonedElement);
      
      // Try direct save first (most compatible)
      await pdfWorker.save();
      
      console.log('PDF download initiated successfully');

      // Reset button
      if (button) {
        setTimeout(() => {
          button.disabled = false;
          button.innerHTML = 'Download Report as PDF';
        }, 1000);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
      
      // Reset button on error
      const button = document.querySelector('.download-pdf-btn') as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = 'Download Report as PDF';
      }
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  // Metric ranges and classifications
  const metricRanges: { [key: string]: MetricRange[] } = {
    'SDNN': [
      { min: 0, max: 30, label: 'Low' },
      { min: 30, max: 96, label: 'Healthy' },
      { min: 96, max: 150, label: 'Optimal' }
    ],
    'RMSSD': [
      { min: 0, max: 20, label: 'Low' },
      { min: 20, max: 90, label: 'Healthy' },
      { min: 90, max: 150, label: 'Optimal' }
    ],
    'PNN50': [
      { min: 0, max: 3, label: 'Low' },
      { min: 3, max: 20, label: 'Normal' },
      { min: 20, max: 90, label: 'Optimal' }
    ],
    'Cardiac Output': [
      { min: 0, max: 4, label: 'Low' },
      { min: 4, max: 8, label: 'Average' },
      { min: 8, max: 15, label: 'Normal' }
    ],
    'Mean Arterial Pressure (MAP)': [
      { min: 0, max: 70, label: 'Low' },
      { min: 70, max: 100, label: 'Normal' },
      { min: 100, max: 150, label: 'High' }
    ],
    'HRV': [
      { min: 0, max: 33, label: 'Low' },
      { min: 33, max: 66, label: 'Normal' },
      { min: 66, max: 100, label: 'Good' }
    ],
    'Heart Rate': [
      { min: 30, max: 55, label: 'Low' },
      { min: 55, max: 100, label: 'Normal' },
      { min: 100, max: 220, label: 'High' }
    ],
    'Oxygen Saturation (SPO2)': [
      { min: 80, max: 91, label: 'VeryLow' },
      { min: 91, max: 94, label: 'Low' },
      { min: 94, max: 100, label: 'Normal' }
    ],
    'BP Diastolic': [
      { min: 30, max: 70, label: 'Low' },
      { min: 70, max: 89, label: 'Normal' },
      { min: 89, max: 120, label: 'High' }
    ],
    'BP Systolic': [
      { min: 50, max: 100, label: 'Low' },
      { min: 100, max: 120, label: 'Normal' },
      { min: 120, max: 220, label: 'High' }
    ],
    'Hba1c Risk': [
      { min: 0, max: 30, label: 'Low' },
      { min: 30, max: 70, label: 'Moderate' },
      { min: 70, max: 100, label: 'Severe' }
    ],
    'Stress Index': [
      { min: 0, max: 0.5, label: 'Low' },
      { min: 0.5, max: 1.5, label: 'Normal' },
      { min: 1.5, max: 5, label: 'Elevated' },
      { min: 5, max: 9, label: 'High' },
      { min: 9, max: 100, label: 'Very High' }
    ],
    'Respiration Rate': [
      { min: 6, max: 12, label: 'Low' },
      { min: 12, max: 21, label: 'Normal' },
      { min: 21, max: 30, label: 'High' }
    ],
    'Hypertension Risk': [
      { min: 0, max: 30, label: 'Low' },
      { min: 30, max: 70, label: 'Moderate' },
      { min: 70, max: 100, label: 'Severe' }
    ],
    'Haemoglobin': [
      { min: 0, max: 13, label: 'Low' },
      { min: 13, max: 17, label: 'Normal' },
      { min: 17, max: 20, label: 'High' }
    ],
    'Hba1c': [
      { min: 0, max: 5.7, label: 'Normal' },
      { min: 5.7, max: 6.5, label: 'Elevated' },
      { min: 6.5, max: 12, label: 'High' }
    ],
    'Health Score': [
      { min: 0, max: 25, label: 'Poor' },
      { min: 25, max: 50, label: 'Fair' },
      { min: 50, max: 75, label: 'Good' },
      { min: 75, max: 100, label: 'Great' }
    ],
    'Heart Utilised': [
      { min: 0, max: 50, label: 'Low' },
      { min: 50, max: 65, label: 'Normal' },
      { min: 65, max: 100, label: 'High' }
    ],
    'Cardiac Load Risk': [
      { min: 0, max: 30, label: 'Low' },
      { min: 30, max: 70, label: 'Moderate' },
      { min: 70, max: 100, label: 'High' }
    ],
    'BMI': [
      { min: 0, max: 18.4, label: 'Low' },
      { min: 18.4, max: 24.9, label: 'Normal' },
      { min: 24.9, max: 29.9, label: 'Overweight' },
      { min: 29.9, max: 40, label: 'Obese' }
    ],
    'Body Fat Percentage': [
      { min: 0, max: 8, label: 'Low' },
      { min: 8, max: 20, label: 'Normal' },
      { min: 20, max: 100, label: 'High' }
    ],
    'BMR': [
      { min: 1000, max: 1399, label: 'Low' },
      { min: 1399, max: 1799, label: 'Normal' },
      { min: 1799, max: 2299, label: 'High' },
      { min: 2299, max: 3000, label: 'Highest' }
    ],
    'TDEE': [
      { min: 1500, max: 1999, label: 'Poor' },
      { min: 1999, max: 2499, label: 'Normal' },
      { min: 2499, max: 3199, label: 'Good' },
      { min: 3199, max: 4500, label: 'Great' }
    ],
    'Total Body Water': [
      { min: 0, max: 45, label: 'Low' },
      { min: 45, max: 60, label: 'Moderate' },
      { min: 60, max: 100, label: 'Optimal' }
    ],
    'Blood Volume': [
      { min: 0, max: 4, label: 'Low' },
      { min: 4, max: 6, label: 'Normal' },
      { min: 6, max: 10, label: 'High' }
    ],
    'Heart Rate Max': [
      { min: 140, max: 160, label: 'Low' },
      { min: 160, max: 180, label: 'Normal' },
      { min: 180, max: 220, label: 'High' }
    ],
    'Heart Rate Reserve': [
      { min: 0, max: 40, label: 'Low' },
      { min: 40, max: 80, label: 'Normal' },
      { min: 80, max: 150, label: 'High' }
    ],
    'Target HR Rate': [
      { min: 90, max: 120, label: 'Low' },
      { min: 120, max: 150, label: 'Moderate' },
      { min: 150, max: 170, label: 'High' }
    ],
    'VO2 Max': [
      { min: 0, max: 35, label: 'Poor' },
      { min: 35, max: 45, label: 'Fair' },
      { min: 45, max: 55, label: 'Good' },
      { min: 55, max: 80, label: 'Excellent' }
    ]
  };

  // Metric descriptions
  const metricDescriptions: { [key: string]: string } = {
    'SDNN': 'The standard deviation of all of the NN intervals (the time intervals between each heartbeat). Higher numbers usually indicate that your body is coping better with stress.',
    'RMSSD': 'The root mean square of successive intervals between normal heartbeats (RMSSD). RMSSD is obtained by first calculating each successive time intervals between heartbeats in msec.',
    'PNN50': 'PNN50 is the proportion of adjacent N-N intervals differing by more than 50 msec. pNN50 lower than 3% is considered indicative of high risk.',
    'Cardiac Output': 'The amount of blood heart pumps through the circulatory system in a minute. Cardiac output in humans is generally 4-8 L/min in an at-rest.',
    'Mean Arterial Pressure (MAP)': 'MAP is the average arterial pressure throughout one heart cycle. MAP anything between 70 and 100 mmHg is considered to be normal.',
    'HRV': 'HRV is the variation in time between heartbeats, indicating stress and recovery balance.',
    'Heart Rate': 'The heart rate is the number of times heart beats in a minute. A normal resting heart rate for adults ranges from 60 to 100 beats per minute.',
    'Oxygen Saturation (SPO2)': 'Oxygen saturation (SpO2) is the measurement of how much oxygen the blood is carrying as a percentage of the maximum it could carry.',
    'BP Diastolic': 'Blood pressure is a measure of the force that the heart uses to pump blood around the body. Diastolic pressure - the pressure when your heart rests between beats.',
    'BP Systolic': 'Blood pressure is a measure of the force that the heart uses to pump blood around the body. Systolic pressure - the pressure when your heart pushes blood out.',
    'Hba1c Risk': 'HbA1c Risk Score reflects how well your blood sugar has been managed over the past 2–3 months. A higher score indicates greater risk due to poor blood sugar control. Maintaining a lower score helps reduce your chances of developing or worsening diabetes-related complications.',
    'Stress Index': "Body's response to everyday pressures resulting from emotional or physical tension. A Stress Index below 1.5 is considered normal.",
    'Respiration Rate': 'The respiration rate is the number of breaths taken per minute. It is typically measured at rest by counting the chest rises for one minute.',
    'Hypertension Risk': 'Likelihood of developing high blood pressure indicates the risk of hypertension based on factors like lifestyle, genetics, weight, and overall cardiovascular health.',
    'Haemoglobin': 'Hemoglobin is a protein in red blood cells that carries oxygen from the lungs to the body\'s tissues. A normal hemoglobin level for adults ranges from 13.5 to 17.5 g/dl.',
    'Hba1c': 'HbA1c shows the average blood sugar level over the past 2–3 months. It measures how much sugar is attached to hemoglobin in red blood cells and is commonly used to diagnose and monitor diabetes.',
    'Health Score': 'This score is based on the assessments you\'ve completed. Offering a clear snapshot of your current health and fitness. Use it to track progress and target areas for improvement.',
    'Heart Utilised': 'Heart utilization is the percentage of overall heart usage to its maximum peak heart rate. Generally less than 50% utilization at rest and 51-85% utilization at moderate intensity is considered as normal.',
    'Cardiac Load Risk': 'Risk due to heart workload strain indicates the potential for cardiovascular stress or complications based on how hard the heart must work during daily activities or exercise.',
    'BMI': 'BMI is the measure of body fat based on height and weight. The normal range of BMI is 18.5 - 24.9 kg/m2. OW - Over weight',
    'Body Fat Percentage': 'The percentage of your total body weight that is made up of fat mass. It is strongly correlated with obesity-related and cardiovascular health risks. Body fat percentage reference ranges are age and gender specific.',
    'BMR': 'Basal Metabolic Rate (BMR) is the number of calories your body burns daily at rest to maintain essential functions like breathing, circulation, and cellular activity.',
    'TDEE': 'Total Daily Energy Expenditure (TDEE) is an estimate of the total calories burned in a day, including basal metabolism, physical activity, and digestion.',
    'Total Body Water': 'Hydration level of the body indicates the amount of water present in the body, reflecting overall fluid balance, cellular function, and general health status.',
    'Blood Volume': 'Total volume of circulating blood refers to the entire amount of blood flowing through the body, essential for delivering oxygen, nutrients, and maintaining overall cardiovascular health.',
    'Heart Rate Max': 'Maximum heart rate is the highest number of beats per minute your heart can reach during maximum physical exertion. It\'s typically estimated as 220 minus your age.',
    'Heart Rate Reserve': 'Heart Rate Reserve is the difference between your maximum heart rate and resting heart rate. It represents your heart\'s capacity to increase its rate during physical activity and is used to determine exercise intensity zones.',
    'Target HR Rate': 'Target Heart Rate is the ideal heart rate zone for cardiovascular exercise, typically calculated as 50-85% of your maximum heart rate. It helps optimize workout intensity for fitness goals.',
    'VO2 Max': 'VO2 Max is the maximum amount of oxygen your body can utilize during intense exercise. It\'s a key indicator of cardiovascular fitness and aerobic endurance. Higher values indicate better cardiorespiratory fitness.'
  };

  // Metric mapping from API fields to display names
  const metricMapping: { [key: string]: { displayName: string; unit: string } } = {
    'hrv_sdnn': { displayName: 'SDNN', unit: 'ms' },
    'hrv_rmssd': { displayName: 'RMSSD', unit: 'ms' },
    'pNN50_per': { displayName: 'PNN50', unit: '%' },
    'cardiac_out': { displayName: 'Cardiac Output', unit: 'L/min' },
    'map': { displayName: 'Mean Arterial Pressure (MAP)', unit: 'mmHg' },
    'hrvScore': { displayName: 'HRV', unit: '' },
    'heart_rate': { displayName: 'Heart Rate', unit: 'bpm' },
    'heart_rate_bpm': { displayName: 'Heart Rate', unit: 'bpm' },
    'oxy_sat_prcnt': { displayName: 'Oxygen Saturation (SPO2)', unit: '%' },
    'spo2': { displayName: 'Oxygen Saturation (SPO2)', unit: '%' },
    'bp_dia': { displayName: 'BP Diastolic', unit: 'mmHg' },
    'dbp': { displayName: 'BP Diastolic', unit: 'mmHg' },
    'bp_sys': { displayName: 'BP Systolic', unit: 'mmHg' },
    'sbp': { displayName: 'BP Systolic', unit: 'mmHg' },
    'hba1c_risk_percentage': { displayName: 'Hba1c Risk', unit: '%' },
    'stress': { displayName: 'Stress Index', unit: '' },
    'stress_index': { displayName: 'Stress Index', unit: '' },
    'resp_rate': { displayName: 'Respiration Rate', unit: 'brpm' },
    'respiration_rate_bpm': { displayName: 'Respiration Rate', unit: 'brpm' },
    'hypertension_risk_percentage': { displayName: 'Hypertension Risk', unit: '%' },
    'hemoglobin': { displayName: 'Haemoglobin', unit: 'g/dL' },
    'Hemoglobin': { displayName: 'Haemoglobin', unit: 'g/dL' },
    'hba1c': { displayName: 'Hba1c', unit: '%' },
    'overall_health_score': { displayName: 'Health Score', unit: '' },
    'heart_utilized': { displayName: 'Heart Utilised', unit: '%' },
    'cardiac_load_risk_percentage': { displayName: 'Cardiac Load Risk', unit: '%' },
    'bmi': { displayName: 'BMI', unit: '' },
    'bodyfat': { displayName: 'Body Fat Percentage', unit: '%' },
    'bmr': { displayName: 'BMR', unit: 'kcal/day' },
    'tdee': { displayName: 'TDEE', unit: 'kcal/day' },
    'tbwp': { displayName: 'Total Body Water', unit: '%' },
    'bloodvolume': { displayName: 'Blood Volume', unit: 'L' },
    'HRMax': { displayName: 'Heart Rate Max', unit: 'bpm' },
    'HRR': { displayName: 'Heart Rate Reserve', unit: 'bpm' },
    'vo2max': { displayName: 'VO2 Max', unit: 'mL/kg/min' }
  };

  const classifyMetric = (metricName: string, value: number): { detailedLabel: string; badge: 'LOW' | 'NORMAL' | 'HIGH' } => {
    const ranges = metricRanges[metricName];
    if (!ranges) {
      return { detailedLabel: 'Unknown', badge: 'NORMAL' };
    }

    for (const range of ranges) {
      if (value > range.min && value <= range.max) {
        const label = range.label;
        // Determine badge based on detailed label
        const lowLabels = ['VeryLow', 'Low'];
        const highLabels = ['Elevated', 'High', 'Severe', 'Overweight', 'Obese', 'Poor', 'Highest', 'Very High'];
        
        let badge: 'LOW' | 'NORMAL' | 'HIGH' = 'NORMAL';
        if (lowLabels.includes(label)) badge = 'LOW';
        else if (highLabels.includes(label)) badge = 'HIGH';
        
        return { detailedLabel: label, badge };
      }
    }

    return { detailedLabel: 'Out of Range', badge: 'HIGH' };
  };

  const getRangeText = (metricName: string): string => {
    const ranges = metricRanges[metricName];
    if (!ranges) return 'N/A';
    
    return ranges.map(r => `${r.label}: ${r.min < 1 ? r.min : Math.round(r.min)}-${r.max < 1 ? r.max : Math.round(r.max)}`).join('; ');
  };

  const getMeaningAndSuggestion = (metricName: string, badge: string, detailedLabel: string): { meaning?: string; suggestion?: string } => {
    if (badge === 'NORMAL') return {};

    const meanings: { [key: string]: { [status: string]: { meaning: string; suggestion: string } } } = {
      'Heart Rate': {
        'LOW': { meaning: 'Heart rate is below normal range', suggestion: 'May indicate high fitness or medical condition - consult physician if symptomatic' },
        'HIGH': { meaning: 'Heart rate is elevated', suggestion: 'Practice relaxation techniques; seek medical advice if persistent' }
      },
      'Oxygen Saturation (SPO2)': {
        'LOW': { meaning: 'Blood oxygen level is low', suggestion: 'Seek immediate medical attention if readings persist' },
        'VeryLow': { meaning: 'Blood oxygen level is critically low', suggestion: 'Seek immediate medical attention' }
      },
      'BP Systolic': {
        'LOW': { meaning: 'Systolic blood pressure is low', suggestion: 'Stay hydrated; consult doctor if experiencing dizziness' },
        'HIGH': { meaning: 'Systolic blood pressure is elevated', suggestion: 'Reduce sodium intake; monitor regularly; consult physician' }
      },
      'BP Diastolic': {
        'LOW': { meaning: 'Diastolic blood pressure is low', suggestion: 'Ensure adequate hydration and nutrition' },
        'HIGH': { meaning: 'Diastolic blood pressure is elevated', suggestion: 'Lifestyle modifications recommended; consult healthcare provider' }
      },
      'BMI': {
        'LOW': { meaning: 'Body mass index below healthy range', suggestion: 'Consider nutritional assessment; increase caloric intake if appropriate' },
        'Overweight': { meaning: 'Body mass index indicates excess weight', suggestion: 'Balanced diet and regular exercise recommended' },
        'Obese': { meaning: 'Body mass index indicates obesity', suggestion: 'Consult healthcare provider for weight management plan' }
      },
      'Stress Index': {
        'Elevated': { meaning: 'Stress levels are elevated', suggestion: 'Practice stress management techniques like meditation or yoga' },
        'High': { meaning: 'Stress levels are high', suggestion: 'Prioritize rest; consider professional stress management support' },
        'Very High': { meaning: 'Stress levels are very high', suggestion: 'Seek professional help; implement immediate stress reduction strategies' }
      },
      'HRV': {
        'LOW': { meaning: 'Heart rate variability is low', suggestion: 'Improve sleep quality; reduce stress; increase physical activity' }
      },
      'SDNN': {
        'LOW': { meaning: 'Heart rate variability metric is low', suggestion: 'Focus on recovery; improve sleep and stress management' }
      },
      'RMSSD': {
        'LOW': { meaning: 'Parasympathetic activity is reduced', suggestion: 'Enhance recovery through better sleep and relaxation' }
      },
      'Hba1c': {
        'Elevated': { meaning: 'Pre-diabetic range detected', suggestion: 'Dietary changes recommended; consult healthcare provider' },
        'High': { meaning: 'Diabetic range detected', suggestion: 'Immediate medical consultation required for diabetes management' }
      },
      'Hypertension Risk': {
        'Moderate': { meaning: 'Moderate risk for hypertension', suggestion: 'Lifestyle modifications; regular BP monitoring recommended' },
        'Severe': { meaning: 'High risk for hypertension', suggestion: 'Consult physician immediately for evaluation and management' }
      },
      'VO2 Max': {
        'Poor': { meaning: 'Cardiovascular fitness is low', suggestion: 'Start gradual aerobic exercise program' },
        'Fair': { meaning: 'Cardiovascular fitness is below optimal', suggestion: 'Increase cardio exercise frequency and intensity gradually' }
      }
    };

    const metricMeanings = meanings[metricName];
    if (!metricMeanings) {
      // Generic meanings for metrics not specifically defined
      if (badge === 'LOW') {
        return { 
          meaning: `${metricName} is below normal range`, 
          suggestion: 'Consult healthcare provider for personalized advice' 
        };
      } else if (badge === 'HIGH') {
        return { 
          meaning: `${metricName} is above normal range`, 
          suggestion: 'Consider lifestyle modifications; consult healthcare provider' 
        };
      }
      return {};
    }

    return metricMeanings[detailedLabel] || metricMeanings[badge] || {};
  };

  // Process all metrics from data
  const processMetrics = (): { [metricName: string]: MetricInfo } => {
    const metrics: { [metricName: string]: MetricInfo } = {};
    const processedData = { 
      ...data.vitals, 
      ...data.original_vitals,
      ...data.metadata?.physiological_scores,
      ...data.metadata?.heart_scores,
      ...data.metadata?.cardiovascular,
      ...data.metadata?.glucose_info
    };

    // Track which display names we've already processed to avoid duplicates
    const processedDisplayNames = new Set<string>();

    Object.entries(metricMapping).forEach(([apiField, config]) => {
      // Skip if we've already processed this display name
      if (processedDisplayNames.has(config.displayName)) {
        return;
      }

      const value = processedData[apiField];
      
      if (value === null || value === undefined || value === '--' || isNaN(Number(value))) {
        return; // Skip invalid values
      }

      const numericValue = Number(value);
      const classification = classifyMetric(config.displayName, numericValue);
      const rangeText = getRangeText(config.displayName);
      const { meaning, suggestion } = getMeaningAndSuggestion(config.displayName, classification.badge, classification.detailedLabel);

      metrics[config.displayName] = {
        value: numericValue,
        unit: config.unit,
        rangeText,
        badge: classification.badge,
        detailedLabel: classification.detailedLabel,
        meaning,
        suggestion
      };

      processedDisplayNames.add(config.displayName);
    });

    return metrics;
  };

  // Extract user info from metadata or userData prop
  const userAge = userData?.age || data.metadata?.physiological_scores?.age || data.age;
  const userGender = userData?.gender || data.metadata?.physiological_scores?.gender || data.gender;
  const userName = userData?.name || data.user_name || data.name;
  const userHeight = userData?.height || data.metadata?.physiological_scores?.height;
  const userWeight = userData?.weight || data.metadata?.physiological_scores?.weight;
  const scanDate = data.scan_completion_time || data.timestamp || data.scan_date;

  const allMetrics = processMetrics();
  const needsAttention = Object.entries(allMetrics).filter(([_, m]) => m.badge !== 'NORMAL');
  const inRange = Object.entries(allMetrics).filter(([_, m]) => m.badge === 'NORMAL');

  const getBadgeVariant = (badge: string) => {
    switch (badge) {
      case 'LOW': return 'warning';
      case 'HIGH': return 'danger';
      default: return 'success';
    }
  };

  const getScorePosition = (score: number, min: number, max: number): number => {
    return ((score - min) / (max - min)) * 100;
  };

  const getScaleData = (metricName: string, value: number) => {
    const ranges = metricRanges[metricName];
    if (!ranges || ranges.length === 0) return null;

    const min = ranges[0].min;
    const max = ranges[ranges.length - 1].max;
    const position = getScorePosition(value, min, max);

    return { min, max, position, ranges };
  };

  const getStatusColor = (detailedLabel: string): string => {
    const lowLabels = ['VeryLow', 'Low', 'Poor'];
    const fairLabels = ['Fair', 'Moderate', 'Average'];
    const goodLabels = ['Good', 'Normal', 'Healthy', 'Optimal'];
    const greatLabels = ['Great', 'Excellent'];
    const warningLabels = ['Elevated', 'High', 'Severe', 'Overweight', 'Obese', 'Very High'];

    if (lowLabels.includes(detailedLabel)) return '#FFA800';
    if (fairLabels.includes(detailedLabel)) return '#FFD700';
    if (goodLabels.includes(detailedLabel)) return '#50CD89';
    if (greatLabels.includes(detailedLabel)) return '#009EF7';
    if (warningLabels.includes(detailedLabel)) return '#F1416C';
    
    return '#50CD89'; // default to green
  };

  return (
    <>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }

          .hide-on-print {
            display: none !important;
          }

          .page-break-before {
            page-break-before: always;
          }

          .page-break-after {
            page-break-after: always;
          }

          .page-break-inside-avoid {
            page-break-inside: avoid;
          }

          .card {
            border: 1px solid #dee2e6 !important;
            box-shadow: none !important;
            margin-bottom: 1rem !important;
            page-break-inside: avoid;
          }

          .card-header {
            background-color: #f8f9fa !important;
            border-bottom: 1px solid #dee2e6 !important;
          }

          .border-end {
            border-right: 1px solid #dee2e6 !important;
          }

          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
          }

          .svg-icon {
            display: none;
          }

          .btn {
            display: none !important;
          }
        }
      `}</style>

      <div ref={printRef}>
      {/* Title */}
      <Card className="mb-4 border-primary">
        <Card.Body className="text-center py-4">
          <h2 className="text-primary mb-0">
            <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-2x me-2' />
            Face Scan Health Report
          </h2>
        </Card.Body>
      </Card>

      {/* Scan Information - Moved to Top */}
      <Card className="mb-4">
        <Card.Header>
          <Card.Title className="mb-0">
            <KTSVG path='/media/icons/duotune/communication/com006.svg' className='svg-icon-3 me-2' />
            Scan Information
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            {userName && userName !== '--' && (
              <Col xs={6} md={3}>
                <p className="text-muted mb-1 small">Name</p>
                <h6 className="mb-0">{userName}</h6>
              </Col>
            )}
            {userAge && userAge !== '--' && (
              <Col xs={6} md={3}>
                <p className="text-muted mb-1 small">Age</p>
                <h6 className="mb-0">{userAge} years</h6>
              </Col>
            )}
            {userGender && userGender !== '--' && (
              <Col xs={6} md={3}>
                <p className="text-muted mb-1 small">Gender</p>
                <h6 className="mb-0">{userGender.charAt(0).toUpperCase() + userGender.slice(1)}</h6>
              </Col>
            )}
            {userHeight && userHeight !== '--' && (
              <Col xs={6} md={3}>
                <p className="text-muted mb-1 small">Height</p>
                <h6 className="mb-0">{userHeight} cm</h6>
              </Col>
            )}
            {userWeight && userWeight !== '--' && (
              <Col xs={6} md={3}>
                <p className="text-muted mb-1 small">Weight</p>
                <h6 className="mb-0">{userWeight} kg</h6>
              </Col>
            )}
            {scanDate && scanDate !== '--' && (
              <Col xs={6} md={3}>
                <p className="text-muted mb-1 small">Scan Date & Time</p>
                <h6 className="mb-0">
                  {isNaN(Number(scanDate)) 
                    ? new Date(scanDate).toLocaleString()
                    : new Date(Number(scanDate) * 1000).toLocaleString()
                  }
                </h6>
              </Col>
            )}
            {data.scan_id && (
              <Col xs={12}>
                <p className="text-muted mb-0 small">
                  <strong>Scan ID:</strong> <code className="text-primary">{data.scan_id}</code>
                </p>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Wellness Score - Prominent Display */}
      {data.wellness_score !== undefined && data.wellness_score !== '--' && (
        <Card className="mb-4">
          <Card.Header>
            <Card.Title className="mb-0">Overall Wellness Score</Card.Title>
          </Card.Header>
          <Card.Body className="text-center py-5">
            <h1 className={`display-1 text-${getScoreColor(data.wellness_score)} mb-3`}>
              {data.wellness_score}
            </h1>
            <Badge bg={getScoreColor(data.wellness_score)} className="fs-6 px-3 py-2">
              {data.wellness_score >= 80 ? 'Excellent' : data.wellness_score >= 60 ? 'Good' : 'Needs Attention'}
            </Badge>
            <p className="text-muted mt-3 mb-0 small">
              Note: Wellness Score is a comprehensive measure combining multiple health factors, different from individual Health Score metric shown below.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Quick Summary */}
      <Card className="mb-4 page-break-inside-avoid">
        <Card.Header>
          <Card.Title className="mb-0">Quick Summary</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-4">
            {/* Needs Attention */}
            <Col xs={12} md={6}>
              <div className="p-3 border border-warning rounded bg-light-warning">
                <h6 className="text-warning mb-3">
                  <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-3 me-2' />
                  Needs Attention ({needsAttention.length})
                </h6>
                {needsAttention.length === 0 ? (
                  <p className="text-muted mb-0 small">All metrics are within normal range</p>
                ) : (
                  <ul className="mb-0 ps-3">
                    {needsAttention.slice(0, 5).map(([metricName, metric], idx) => (
                      <li key={idx} className="text-muted small mb-1">
                        {metricName}: <Badge bg={getBadgeVariant(metric.badge)} className="me-1">{metric.badge}</Badge> {metric.detailedLabel}
                      </li>
                    ))}
                    {needsAttention.length > 5 && (
                      <li className="text-muted small">...and {needsAttention.length - 5} more</li>
                    )}
                  </ul>
                )}
              </div>
            </Col>

            {/* In Range */}
            <Col xs={12} md={6}>
              <div className="p-3 border border-success rounded bg-light-success">
                <h6 className="text-success mb-3">
                  <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-3 me-2' />
                  In Range ({inRange.length})
                </h6>
                {inRange.length === 0 ? (
                  <p className="text-muted mb-0 small">No metrics in normal range</p>
                ) : (
                  <ul className="mb-0 ps-3">
                    {inRange.slice(0, 5).map(([metricName, metric], idx) => (
                      <li key={idx} className="text-muted small mb-1">
                        {metricName}: {metric.detailedLabel}
                      </li>
                    ))}
                    {inRange.length > 5 && (
                      <li className="text-muted small">...and {inRange.length - 5} more</li>
                    )}
                  </ul>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Detailed Metrics - Card Layout */}
      <Card className="mb-4 page-break-before">
        <Card.Header>
          <Card.Title className="mb-0">Detailed Health Metrics</Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            {Object.entries(allMetrics).map(([metricName, metricData]) => {
              const scaleData = getScaleData(metricName, metricData.value || 0);
              const statusColor = getStatusColor(metricData.detailedLabel);
              const description = metricDescriptions[metricName] || '';
              
              return (
                <Col xs={12} key={metricName}>
                  <Card className="border page-break-inside-avoid" style={{ borderRadius: '12px' }}>
                    <Card.Body className="p-4">
                      <Row>
                        {/* Left Side - Metric Info */}
                        <Col xs={12} md={4} className="border-end">
                          {/* Metric Name */}
                          <h5 className="mb-2" style={{ fontWeight: '600' }}>
                            {metricName}
                          </h5>
                          
                          {/* Score and Status */}
                          <div className="d-flex align-items-baseline mb-3">
                            <h1 className="mb-0 me-2" style={{ fontSize: '3rem', fontWeight: '700' }}>
                              {metricData.value !== null ? Math.round(metricData.value) : 'N/A'}
                            </h1>
                            {metricData.unit && (
                              <span className="text-muted" style={{ fontSize: '1.1rem' }}>{metricData.unit}</span>
                            )}
                          </div>

                          {/* Status Label */}
                          <div className="mb-3">
                            <Badge 
                              style={{ 
                                backgroundColor: statusColor, 
                                color: 'white',
                                fontSize: '0.95rem',
                                fontWeight: '500',
                                padding: '8px 16px',
                                borderRadius: '8px'
                              }}
                            >
                              {metricData.detailedLabel}
                            </Badge>
                          </div>

                          {/* Scale Visualization */}
                          {scaleData && (
                            <div>
                              <div className="d-flex justify-content-between mb-2" style={{ fontSize: '0.8rem' }}>
                                {scaleData.ranges.map((range, idx) => (
                                  <span key={idx} className="text-muted">
                                    {range.label}
                                  </span>
                                ))}
                              </div>
                              
                              {/* Scale Bar */}
                              <div style={{ position: 'relative', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                                {/* Background gradient representing ranges */}
                                <div 
                                  style={{ 
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    background: `linear-gradient(to right, 
                                      ${scaleData.ranges.map((range, idx) => {
                                        const colors = ['#FFA800', '#FFD700', '#50CD89', '#009EF7'];
                                        const percent = ((idx + 1) / scaleData.ranges.length) * 100;
                                        return `${colors[idx] || '#50CD89'} ${percent}%`;
                                      }).join(', ')})`,
                                    borderRadius: '5px'
                                  }}
                                />
                              </div>

                              {/* Scale Numbers */}
                              <div className="d-flex justify-content-between mt-2" style={{ fontSize: '0.75rem' }}>
                                <span className="text-muted">{scaleData.min}</span>
                                <span className="text-muted">{Math.round((scaleData.min + scaleData.max) / 2)}</span>
                                <span className="text-muted">{scaleData.max}</span>
                              </div>
                            </div>
                          )}
                        </Col>

                        {/* Right Side - Description and Notes */}
                        <Col xs={12} md={8}>
                          {/* Description */}
                          {description && (
                            <div className="mb-3">
                              <h6 className="text-muted mb-2" style={{ fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase' }}>
                                Description
                              </h6>
                              <p className="text-dark mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                                {description}
                              </p>
                            </div>
                          )}

                          {/* Additional Notes */}
                          {metricData.badge !== 'NORMAL' && (metricData.meaning || metricData.suggestion) && (
                            <div className="mt-3 pt-3 border-top">
                              {metricData.meaning && (
                                <div className="mb-2">
                                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase' }}>
                                    Note
                                  </h6>
                                  <p className="text-dark mb-0" style={{ fontSize: '0.9rem' }}>
                                    {metricData.meaning}
                                  </p>
                                </div>
                              )}
                              {metricData.suggestion && (
                                <div className="mt-2">
                                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase' }}>
                                    Recommendation
                                  </h6>
                                  <p className="text-dark mb-0" style={{ fontSize: '0.9rem' }}>
                                    {metricData.suggestion}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>

      {/* Skin Scan Results */}
      {data.skinScan?.skin_report && (
        <Card className="mb-4 border-warning">
          <Card.Header className="bg-warning-subtle">
            <Card.Title className="mb-0 d-flex align-items-center">
              <KTSVG path='/media/icons/duotune/general/gen049.svg' className='svg-icon-3 me-2' />
              Skin Analysis
            </Card.Title>
          </Card.Header>
          <Card.Body>
            {/* Skin Score Overview */}
            <div className="text-center mb-4 pb-4 border-bottom">
              <h2 className={`display-4 text-${getScoreColor(data.skinScan.skin_report.skin_score)} mb-2`}>
                {data.skinScan.skin_report.skin_score}/100
              </h2>
              <Badge bg={getScoreColor(data.skinScan.skin_report.skin_score)} className="fs-6 px-3 py-2 mb-2">
                {data.skinScan.skin_report.skin_grade.toUpperCase()}
              </Badge>
              <p className="text-muted mb-0">
                <strong>Estimated Skin Age:</strong> {data.skinScan.skin_report.estimated_skin_age} years
              </p>
            </div>

            {/* Skin Parameters */}
            <h5 className="mb-4">Skin Parameters</h5>
            <Row className="g-3 mb-4">
              {Object.entries(data.skinScan.skin_report.parameters).map(([key, param]: [string, any]) => (
                <Col xs={12} key={key}>
                  <Card className="border" style={{ borderRadius: '12px' }}>
                    <Card.Body className="p-4">
                      <Row>
                        {/* Left Side - Parameter Info */}
                        <Col xs={12} md={4} className="border-end">
                          {/* Parameter Name */}
                          <h5 className="mb-2" style={{ fontWeight: '600' }}>
                            {key}
                          </h5>
                          
                          {/* Score Display */}
                          <div className="d-flex align-items-baseline mb-3">
                            <h1 className="mb-0 me-2" style={{ fontSize: '3rem', fontWeight: '700' }}>
                              {param.score}
                            </h1>
                            <span className="text-muted" style={{ fontSize: '1.1rem' }}>/ {param.maximum_score}</span>
                          </div>

                          {/* Status Label */}
                          <div className="mb-3">
                            <Badge 
                              style={{ 
                                backgroundColor: getStatusColor(param.label), 
                                color: 'white',
                                fontSize: '0.95rem',
                                fontWeight: '500',
                                padding: '8px 16px',
                                borderRadius: '8px'
                              }}
                            >
                              {param.label}
                            </Badge>
                          </div>

                          {/* Progress Bar */}
                          <div>
                            <div className="d-flex justify-content-between mb-2" style={{ fontSize: '0.8rem' }}>
                              <span className="text-muted">Poor</span>
                              <span className="text-muted">Fair</span>
                              <span className="text-muted">Good</span>
                            </div>
                            
                            <div style={{ position: 'relative', height: '10px', borderRadius: '5px', overflow: 'hidden', backgroundColor: '#E4E6EF' }}>
                              <div 
                                style={{ 
                                  position: 'absolute',
                                  width: `${(param.score / param.maximum_score) * 100}%`,
                                  height: '100%',
                                  background: `linear-gradient(to right, #FFA800, #FFD700, #50CD89)`,
                                  borderRadius: '5px',
                                  transition: 'width 0.3s ease'
                                }}
                              />
                            </div>

                            <div className="d-flex justify-content-between mt-2" style={{ fontSize: '0.75rem' }}>
                              <span className="text-muted">0</span>
                              <span className="text-muted">{Math.round(param.maximum_score / 2)}</span>
                              <span className="text-muted">{param.maximum_score}</span>
                            </div>
                          </div>
                        </Col>

                        {/* Right Side - Description */}
                        <Col xs={12} md={8}>
                          <div>
                            <h6 className="text-muted mb-2" style={{ fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase' }}>
                              Assessment
                            </h6>
                            <p className="text-dark mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                              {param.comment}
                            </p>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Recommendations */}
            {data.skinScan.skin_report.summary_and_recommendations && (
              <div>
                <h5 className="mb-3">Skincare Recommendations</h5>
                <Row className="g-3">
                  {Object.entries(data.skinScan.skin_report.summary_and_recommendations).map(([routine, steps]: [string, any]) => (
                    <Col xs={12} md={6} key={routine}>
                      <Card className="h-100 border-0 bg-light">
                        <Card.Body>
                          <h6 className="text-primary mb-3 fw-bold">{routine}</h6>
                          <ul className="mb-0 ps-3">
                            {steps.map((step: string, index: number) => (
                              <li key={index} className="text-muted small mb-2">{step}</li>
                            ))}
                          </ul>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Important Note */}
      <Alert variant="info" className="mb-4">
        <Alert.Heading className="h6">Important Notice</Alert.Heading>
        <p className="mb-0 small">
          This report is for informational purposes only and does not constitute medical advice or diagnosis. 
          If you have concerns about any of these measurements, please consult with a qualified healthcare professional for proper evaluation and guidance.
        </p>
      </Alert>

      {/* Action Buttons */}
      <Row className="g-3 hide-on-print">
        {onNewScan && (
          <Col xs={12} md={6}>
            <Button variant="primary" className="w-100" onClick={onNewScan}>
              <KTSVG path='/media/icons/duotune/arrows/arr021.svg' className='svg-icon-3 me-2' />
              New Scan
            </Button>
          </Col>
        )}
        <Col xs={12} md={onNewScan ? 6 : 12}>
          <Button 
            variant="outline-primary" 
            className="w-100 download-pdf-btn" 
            onClick={handleDownloadPDF}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr091.svg' className='svg-icon-3 me-2' />
            Download Report as PDF
          </Button>
        </Col>
      </Row>
    </div>
    </>
  );
};

export default ScanResults;

